/**
 * DealNotes — Timestamped deal notes with one-click copy.
 *
 * Features:
 * - Add notes with different types (note, call summary, meeting notes, etc.)
 * - Each note has its own copy button
 * - Mark notes as synced to Salesforce
 * - Timeline view sorted by date
 */

import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquare, Phone, Video, AlertTriangle, ArrowRight,
  Plus, Trash2, Loader2, Check, Clock
} from 'lucide-react';
import apiClient from '../../lib/apiClient';
import CopyButton from './CopyButton';
import './DealNotes.css';

const NOTE_TYPES = [
  { value: 'note', label: 'Note', icon: MessageSquare },
  { value: 'call_summary', label: 'Call Summary', icon: Phone },
  { value: 'meeting_notes', label: 'Meeting Notes', icon: Video },
  { value: 'risk_alert', label: 'Risk Alert', icon: AlertTriangle },
  { value: 'next_steps', label: 'Next Steps', icon: ArrowRight },
];

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateOnly(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

function DealNotes({ opportunityId, notes = [], onUpdate }) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', note_type: 'note' });

  // Optimistic (UI-responsiveness review 2026-08-08, StepChecklist pattern):
  // notes appear / flip / leave on click; onMutate patches the opportunity
  // cache with rollback, and the invalidate on settle is the truth-up.
  const patchNotes = async (fn) => {
    await queryClient.cancelQueries({ queryKey: ['opportunity', opportunityId] });
    const previous = queryClient.getQueryData(['opportunity', opportunityId]);
    queryClient.setQueryData(['opportunity', opportunityId], (prev) => (
      prev && Array.isArray(prev.notes) ? { ...prev, notes: fn(prev.notes) } : prev
    ));
    return { previous };
  };
  const rollback = (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(['opportunity', opportunityId], ctx.previous); };
  const settle = () => {
    queryClient.invalidateQueries({ queryKey: ['opportunity', opportunityId] });
    onUpdate?.();
  };

  const addMutation = useMutation({
    mutationFn: (note) => apiClient.post(`/api/opportunities/${opportunityId}/notes`, note),
    onMutate: (note) => {
      // The form closes and the note is on the list immediately; failure
      // rolls back and reopens the form with the text intact.
      setIsAdding(false);
      setNewNote({ content: '', note_type: 'note' });
      return patchNotes((ns) => [{ id: 'tmp-' + Date.now(), ...note, created_at: new Date().toISOString(), sf_synced: false }, ...ns])
        .then((ctx) => ({ ...ctx, note }));
    },
    onError: (e, v, ctx) => {
      rollback(e, v, ctx);
      if (ctx?.note) { setNewNote(ctx.note); setIsAdding(true); }
    },
    onSettled: settle,
  });

  const updateMutation = useMutation({
    mutationFn: ({ noteId, updates }) => apiClient.put(`/api/opportunities/${opportunityId}/notes/${noteId}`, updates),
    onMutate: ({ noteId, updates }) => patchNotes((ns) => ns.map((n) => (n.id === noteId ? { ...n, ...updates } : n))),
    onError: rollback,
    onSettled: settle,
  });

  const deleteMutation = useMutation({
    mutationFn: (noteId) => apiClient.del(`/api/opportunities/${opportunityId}/notes/${noteId}`),
    onMutate: (noteId) => patchNotes((ns) => ns.filter((n) => n.id !== noteId)),
    onError: rollback,
    onSettled: settle,
  });

  const handleAddNote = (e) => {
    e.preventDefault();
    if (!newNote.content.trim()) return;
    addMutation.mutate(newNote);
  };

  const markSynced = (noteId) => {
    updateMutation.mutate({ noteId, updates: { sf_synced: true } });
  };

  // Generate copy text for all notes (for bulk copy)
  const generateAllNotesCopy = () => {
    const lines = ['RECENT ACTIVITY', ''];
    for (const note of notes.slice(0, 10)) {
      const typeLabel = NOTE_TYPES.find(t => t.value === note.note_type)?.label || 'Note';
      lines.push(`${formatDateOnly(note.created_at)} — ${typeLabel}`);
      lines.push(note.content);
      lines.push('');
    }
    return lines.join('\n');
  };

  // Generate copy text for single note
  const generateNoteCopy = (note) => {
    const typeLabel = NOTE_TYPES.find(t => t.value === note.note_type)?.label || 'Note';
    return `${formatDateOnly(note.created_at)} — ${typeLabel}\n${note.content}`;
  };

  return (
    <div className="dn-container">
      <div className="dn-header">
        <h3 className="dn-title">Deal Notes</h3>
        <div className="dn-actions">
          {notes.length > 0 && (
            <CopyButton
              text={generateAllNotesCopy()}
              label="Copy All"
              showLabel
              className="copy-btn-pill"
            />
          )}
          <button className="dn-btn dn-btn-add" onClick={() => setIsAdding(true)}>
            <Plus size={14} /> Add note
          </button>
        </div>
      </div>

      {/* Add note form */}
      {isAdding && (
        <form className="dn-add-form" onSubmit={handleAddNote}>
          <div className="dn-type-select">
            {NOTE_TYPES.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.value}
                  type="button"
                  className={`dn-type-btn ${newNote.note_type === type.value ? 'active' : ''}`}
                  onClick={() => setNewNote(n => ({ ...n, note_type: type.value }))}
                >
                  <Icon size={12} />
                  {type.label}
                </button>
              );
            })}
          </div>
          <textarea
            placeholder={
              newNote.note_type === 'call_summary' ? 'Summarize the call...' :
              newNote.note_type === 'meeting_notes' ? 'Key takeaways from the meeting...' :
              newNote.note_type === 'risk_alert' ? 'Describe the risk...' :
              newNote.note_type === 'next_steps' ? 'What needs to happen next...' :
              'Add a note about this opportunity...'
            }
            value={newNote.content}
            onChange={(e) => setNewNote(n => ({ ...n, content: e.target.value }))}
            rows={4}
            autoFocus
          />
          <div className="dn-add-actions">
            <button type="button" className="dn-btn dn-btn-cancel" onClick={() => setIsAdding(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="dn-btn dn-btn-save"
              disabled={!newNote.content.trim()}
            >
              Save note
            </button>
          </div>
        </form>
      )}

      {/* Notes timeline */}
      <div className="dn-timeline">
        {notes.map((note) => {
          const noteType = NOTE_TYPES.find(t => t.value === note.note_type);
          const Icon = noteType?.icon || MessageSquare;

          return (
            <div key={note.id} className={`dn-note dn-note-${note.note_type}`}>
              <div className="dn-note-icon">
                <Icon size={14} />
              </div>
              <div className="dn-note-content">
                <div className="dn-note-header">
                  <span className="dn-note-type">{noteType?.label || 'Note'}</span>
                  <span className="dn-note-date">{formatDateTime(note.created_at)}</span>
                </div>
                <p className="dn-note-text">{note.content}</p>
                <div className="dn-note-footer">
                  <div className="dn-note-status">
                    {note.sf_synced ? (
                      <span className="dn-synced">
                        <Check size={10} /> Synced to SF
                      </span>
                    ) : (
                      <button
                        className="dn-mark-synced"
                        onClick={() => markSynced(note.id)}
                      >
                        <Clock size={10} /> Mark as synced
                      </button>
                    )}
                  </div>
                  <div className="dn-note-actions">
                    <CopyButton
                      text={generateNoteCopy(note)}
                      size={12}
                    />
                    <button
                      className="dn-note-delete"
                      onClick={() => deleteMutation.mutate(note.id)}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {notes.length === 0 && !isAdding && (
          <div className="dn-empty">
            No notes yet. Add notes to track your deal progress.
          </div>
        )}
      </div>
    </div>
  );
}

export default DealNotes;
